<template lang="pug">
.dropdownSelectComponent(
  v-click-outside="onClickOutside",
  :class="inputClasses"
)
  .handle(@click="onClick")
    .text {{ modelValue !== null ? valueLabel : placeholderText }}
    .icon
      ui-svg-icon( name="chevron" )
  template(v-if="isOpened")
    .dropdown
      .options
        .option(
            v-for="option in options",
            :key="option.value"
          )
          dropdown-select-option(
            :option="option",
            :is-selected="modelValue === option.value",
            :is-disabled="isDisabled",
            @select="onOptionSelect(option.value)",
            @deselect="onOptionDeselect"
          )
  .error(
    v-for="error in v$?.$errors"
    :key="error.$validator"
  ) {{ errors[error.$validator] }}
</template>

<script>
import ClickOutsideDirective from '@/_core/directives/click-outside'
import DropdownSelectOption from './UiDropdownSelectOption'

export default {
  components: {
    DropdownSelectOption
  },
  directives: {
    'click-outside': ClickOutsideDirective
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    isDisabled: Boolean,
    isResponsive: Boolean,
    placeholderText: {
      type: String,
      default: 'Выбрать'
    },
    allowEmpty: Boolean,
    v$: {
      type: Object,
      default: () => {}
    },
    errors: {
      type: Object,
      default: () => {}
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      isOpened: false,
      searchQuery: ''
    }
  },
  computed: {
    valueLabel () {
      const selectedOption = this.options.find(o => o.value === this.modelValue)
      return selectedOption ? selectedOption.label : ''
    },
    openedCssClass () {
      return this.isOpened ? '_opened' : ''
    },
    inputClasses () {
      return [
        this.openedCssClass,
        {
          _disabled: this.isDisabled,
          _responsive: this.isResponsive,
          _error: this.v$?.$error
        }
      ]
    }
  },
  methods: {
    emitUpdate (value) {
      this.$emit('update:modelValue', value)
    },
    close () {
      this.isOpened = false
    },
    toggle () {
      this.isOpened = !this.isOpened
    },
    onClick () {
      if (!this.isLoading && !this.isDisabled) {
        this.toggle()
      }
    },
    onOptionSelect (value) {
      if (!this.isLoading && !this.isDisabled) {
        this.close()
        this.emitUpdate(value)
      }
    },
    onOptionDeselect () {
      if (!this.isLoading && !this.isDisabled && this.allowEmpty) {
        this.close()
        this.emitUpdate(null)
      }
    },
    onClickOutside () {
      if (!this.isLoading && !this.isDisabled) {
        this.close()
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.dropdownSelectComponent
  position: relative
  user-select: none
  width: fit-content

  > .handle
    display: flex
    align-items: center
    padding: 1.5*$u 3*$u
    border-radius: 2.5*$u
    color: $cText
    cursor: pointer
    border: 0.75*$u solid $cDarkBackground
    &:hover
      border-color: $cBlue
    > .text
      @include font('t16-medium')
      overflow-x: hidden
      white-space: nowrap
      text-overflow: ellipsis
      flex: 1

    > .icon
      flex: 0 0 3*$u
      width: 3*$u
      height: 3*$u
      margin-left: 3*$u
      transform-origin: center
      transition: $animationSpeed linear
      z-index: 1000 - 1
      .svgIconComponent
        color: $cDarkGrey

  > .dropdown
    position: absolute
    background-color: $cWhite
    border: 0.75*$u solid $cDarkBackground
    border-radius: 2.5*$u
    padding: 2*$u $u
    top: 100%
    width: 100%
    z-index: 1000
    box-shadow: $boxShadow

    > .search
      padding: 2*$u

    > .options
      overflow-y: auto
      max-height: 124*$u
      padding: 0

      > .option
        &:not(:last-child)
          margin-bottom: 3*$u

  &._opened
    > .handle
      border-color: $cDarkBlue
      > .icon
        transform: rotate(180deg)
        .svgIconComponent
          color: $cDarkBlue

  &._disabled
    > .handle
      cursor: not-allowed

  &._responsive
    width: 100%

  &._error
    > .handle
      border-color: $cOrange

  .error
    @include font('t14-regular')
    color: $cOrange
    margin-top: $u
</style>
